import React, { useEffect } from "react";
import { Images } from "../../utils/imageConstants";
import type { IGetAllLeagues } from "../header/types/header";
import { setActiveTab } from "../../redux/slice/activeTabSlice";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

type DivRef = React.MutableRefObject<HTMLDivElement | null>;

type SearchBarProps = {
  many: boolean;
  showState: boolean;
  /** Controlled query — list is already filtered to matching leagues only. */
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  leagues: IGetAllLeagues[];
  selectedLeagueId: string | null;
  onSelect: (league: IGetAllLeagues) => void;
  popoverRef: DivRef;
  /** SubHeader: global `websearch` request in flight */
  searchLoading?: boolean;
  searchError?: boolean;
};

const SearchBar: React.FC<SearchBarProps> = ({ many, showState, search, setSearch, leagues, selectedLeagueId, onSelect, popoverRef }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSearchActive = search.trim().length > 0;
  /** Two-column cap only when browsing the full list; search shows every league match. */
  const useLargeLayout = many && !isSearchActive;

  useEffect(() => {
    if (!selectedLeagueId && leagues.length) {
      onSelect(leagues[0]);
    }
  }, [leagues, selectedLeagueId, onSelect]);

  return (
    <div ref={popoverRef} className={`league-popover ${showState ? "open" : ""} ${useLargeLayout ? "large" : "small"}`} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Select league">
      <div className="surface">
        <div className="searchbox">
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Leagues..." autoComplete="off" spellCheck={false} aria-label="Search leagues" />
          <img src={Images.search_icon_input} alt="" aria-hidden />
        </div>

        <div className={`list ${leagues?.length > 7 ? "two-col" : "one-col"}`}>
          {leagues?.length === 7 ? (
            // Single column: show all 7 leagues
            leagues.map((league, i) => (
              <div className={`league-names-wrapper ${selectedLeagueId === league.leagueId ? "active" : ""}`} key={league?.leagueId ?? `league-${i}`}>
                <p
                  className={`item ${selectedLeagueId === league.leagueId ? "active" : ""}`}
                  onClick={() => {
                    onSelect(league);
                    dispatch(setActiveTab("home"));
                    navigate(`/my-leagues/${league.leagueId}`);
                  }}
                  title={league?.leagueName}
                >
                  {league?.leagueName}
                </p>
              </div>
            ))
          ) : leagues?.length > 7 ? (
            // Two columns: 7 in first col, 4 in second col + "View All Leagues"
            <>
              {/* First column: 7 leagues */}
              {leagues.slice(0, 7).map((league, i) => (
                <div className={`league-names-wrapper ${selectedLeagueId === league.leagueId ? "active" : ""}`} key={league?.leagueId ?? `league-${i}`}>
                  <p
                    className={`item ${selectedLeagueId === league.leagueId ? "active" : ""}`}
                    onClick={() => {
                      onSelect(league);
                      dispatch(setActiveTab("home"));
                      navigate(`/my-leagues/${league.leagueId}`);
                    }}
                    title={league?.leagueName}
                  >
                    {league?.leagueName}
                  </p>
                </div>
              ))}
              {/* Second column: 4 leagues */}
              {leagues.slice(7, 11).map((league, i) => (
                <div className={`league-names-wrapper ${selectedLeagueId === league.leagueId ? "active" : ""}`} key={league?.leagueId ?? `league-${i + 7}`}>
                  <p
                    className={`item ${selectedLeagueId === league.leagueId ? "active" : ""}`}
                    onClick={() => {
                      onSelect(league);
                      dispatch(setActiveTab("home"));
                      navigate(`/my-leagues/${league.leagueId}`);
                    }}
                    title={league?.leagueName}
                  >
                    {league?.leagueName}
                  </p>
                </div>
              ))}
              {/* View All Leagues in second column */}
              <div className="league-names-wrapper view-more-wrapper">
                <a className="view-all" href="/my-leagues" onClick={() => dispatch(setActiveTab("home"))}>
                  View All Leagues →
                </a>
              </div>
            </>
          ) : (
            // Less than 7: show all in single column
            leagues.map((league, i) => (
              <div className={`league-names-wrapper ${selectedLeagueId === league.leagueId ? "active" : ""}`} key={league?.leagueId ?? `league-${i}`}>
                <p
                  className={`item ${selectedLeagueId === league.leagueId ? "active" : ""}`}
                  onClick={() => {
                    onSelect(league);
                    dispatch(setActiveTab("home"));
                    navigate(`/my-leagues/${league.leagueId}`);
                  }}
                  title={league?.leagueName}
                >
                  {league?.leagueName}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
